import { useMemo } from 'react';
import { SMSDataHooks, SMSDataSharedTypes, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { createThreadDateList } from '../utils/create-thread-date-list';

type UseGetInboxThreadQueryProps = {
  threadId: string;
  locationId: string;
  focusItemId?: string;
  focusItemCreatedAt?: string;
  unreadCount?: number;
  isNew?: boolean;
};

/**
 * Custom hook to fetch and process thread data for a messaging application.
 *
 * @param threadId - The ID of the thread to fetch.
 * @param locationId - The location ID associated with the thread.
 * @param focusItemId - The ID of the item to focus on in the thread.
 * @param focusItemCreatedAt - The creation timestamp of the focus item.
 * @param unreadCount - The number of unread messages in the thread.
 *
 * @returns getThreadQuery - The query object for fetching thread data.
 * @returns threadItems - The list of thread items.
 * @returns latestSmsItem - The latest SMS item in the thread.
 * @returns threadItemsDateList - The thread items grouped by date.
 * @returns firstItemId - The ID of the first item in the thread.
 * @returns firstUnreadMessageId - The ID of the first unread message in the thread.
 * @returns contact - The contact information associated with the thread.
 */
export const useGetThreadData = ({
  threadId,
  locationId,
  focusItemId,
  focusItemCreatedAt,
  unreadCount,
  isNew,
}: UseGetInboxThreadQueryProps) => {
  const { isSmsMessageItem, getThreadItemId } = SMSDataSharedUtils.threadItemsUtils;

  const { getThreadQuery, threadItems } = SMSDataHooks.useGetThreadQueryData({
    threadId,
    locationId,
    focusItemId,
    focusItemCreatedAt,
    enabled: !isNew,
  });

  const latestSmsItem = useMemo(() => {
    const items = getThreadQuery.data?.pages[0]?.thread.items;
    if (!items) return undefined;

    return items.find((item) => isSmsMessageItem(item)) as SMSDataSharedTypes.SMSItem | undefined;
  }, [getThreadQuery.data?.pages]);

  const threadItemsDateList = useMemo(() => createThreadDateList(threadItems), [threadItems]);

  const firstItemId = useMemo(() => {
    const firstItem = threadItems.at(0);
    if (!firstItem) return '';
    return getThreadItemId(firstItem);
  }, [threadItems]);

  const contact = useMemo(() => getThreadQuery.data?.pages?.[0]?.thread.person, [getThreadQuery.data?.pages]);

  const firstUnreadMessageId = useMemo(() => {
    if (!unreadCount) return undefined;
    const unreadItem = threadItems.at(threadItems.length - unreadCount);

    if (!unreadItem) return '';
    return getThreadItemId(unreadItem);
  }, [threadItems, unreadCount]);

  return {
    getThreadQuery,
    threadItems,
    latestSmsItem,
    threadItemsDateList,
    firstItemId,
    firstUnreadMessageId,
    contact,
  };
};
